// Order Picking → demand-aligned capacity pack.
//
// Reuses the Analyze tab's date range / pool selection. The planner can
// edit the shift bands, the equipment pool (incl. the reserve that is
// NOT production capacity), the planning-rate mode and the demand
// percentile. The preview leads with the validation gates, because a
// pack that fails a gate is a capacity scenario, not a rota.

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  CalendarRange, Plus, Trash2, Sparkles, FileSpreadsheet, Wrench,
  CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { getRotaSettings, saveRotaSettings, getRotaPlan, exportRotaPlan } from '@/services/api';

const POOL_NAME = { grf: 'Order pickers (GRF)', grt: 'Reach truck (GRT)' };
const RATE_MODES = [
  { v: 'verified_p25', l: 'Measured P25 (conservative)' },
  { v: 'verified_median', l: 'Measured median' },
  { v: 'verified_p75', l: 'Measured P75 (optimistic)' },
  { v: 'pooled', l: 'Measured pooled (tasks ÷ hours)' },
  { v: 'manual', l: 'Manual rate (flags the pack unverified)' },
];
const PERCENTILES = [
  { v: '50', l: 'Mean day (not advised)' },
  { v: '75', l: 'P75 of real days' },
  { v: '90', l: 'P90 of real days (advised)' },
  { v: '100', l: 'Busiest observed day' },
];

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const GateIcon = ({ status }) => {
  if (status === 'pass') return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />;
  if (status === 'warn') return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />;
  return <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />;
};

const BandRows = ({ pool, bands, onChange }) => (
  <div className="space-y-2 overflow-x-auto min-w-0 [&>div]:min-w-[640px]">
    <div className="grid grid-cols-[70px_1fr_80px_80px_1fr_36px] gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
      <span>Code</span><span>Label</span><span>Start</span><span>End</span><span>Purpose</span><span />
    </div>
    {bands.map((b, i) => (
      <div key={i} className="grid grid-cols-[70px_1fr_80px_80px_1fr_36px] gap-2 items-center">
        <Input value={b.code || ''} data-testid={`rota-band-code-${pool}-${i}`}
               onChange={(e) => onChange(i, { code: e.target.value })} className="h-8" />
        <Input value={b.label || ''} data-testid={`rota-band-label-${pool}-${i}`}
               onChange={(e) => onChange(i, { label: e.target.value })} className="h-8" />
        <Input type="number" min={0} max={24} value={b.start ?? ''} data-testid={`rota-band-start-${pool}-${i}`}
               onChange={(e) => onChange(i, { start: num(e.target.value) })} className="h-8" />
        <Input type="number" min={1} max={30} value={b.end ?? ''} data-testid={`rota-band-end-${pool}-${i}`}
               onChange={(e) => onChange(i, { end: num(e.target.value) })} className="h-8" />
        <Input value={b.purpose || ''} data-testid={`rota-band-purpose-${pool}-${i}`}
               onChange={(e) => onChange(i, { purpose: e.target.value })} className="h-8" />
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                data-testid={`rota-band-remove-${pool}-${i}`}
                onClick={() => onChange(i, null)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ))}
  </div>
);

const PoolSettings = ({ pool, value, onPatch }) => {
  const patchBand = (i, patch) => {
    const bands = [...(value.bands || [])];
    if (patch === null) bands.splice(i, 1);
    else bands[i] = { ...bands[i], ...patch };
    onPatch({ bands });
  };
  const field = (label, key, props = {}, hint) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input className="h-8" value={value[key] ?? ''} data-testid={`rota-${key}-${pool}`}
             onChange={(e) => onPatch({ [key]: e.target.value === '' ? null : num(e.target.value) })}
             {...props} />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
  return (
    <div className="rounded-lg border border-border p-4 space-y-4" data-testid={`rota-settings-${pool}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{POOL_NAME[pool]}</h4>
        <Button variant="outline" size="sm" data-testid={`rota-band-add-${pool}`}
                onClick={() => onPatch({
                  bands: [...(value.bands || []), { code: '', label: `${pool.toUpperCase()}-X`, start: 6, end: 15, purpose: '' }],
                })}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add band
        </Button>
      </div>
      <BandRows pool={pool} bands={value.bands || []} onChange={patchBand} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Equipment</Label>
          <Input className="h-8" value={value.equipment_label || ''} data-testid={`rota-equip-label-${pool}`}
                 onChange={(e) => onPatch({ equipment_label: e.target.value })} />
        </div>
        {field('Physical units', 'equipment_units', { type: 'number', min: 0 })}
        {field('Reserve units', 'equipment_reserve', { type: 'number', min: 0 }, 'defects / charging — not capacity')}
        {field('People planned', 'core_people', { type: 'number', min: 0, placeholder: 'auto' }, 'blank = from demand')}
        {field('Attendance hours', 'attendance_hours', { type: 'number', min: 1, max: 14, step: '0.5' })}
        {field('Net hours / shift', 'net_hours', { type: 'number', min: 1, max: 12, step: '0.5' })}
        {field('Work days / week', 'work_days_per_week', { type: 'number', min: 1, max: 6 })}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Demand basis</Label>
          <Select value={String(value.demand_percentile ?? 90)}
                  onValueChange={(v) => onPatch({ demand_percentile: num(v, 90) })}>
            <SelectTrigger className="h-8" data-testid={`rota-percentile-${pool}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERCENTILES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs text-muted-foreground">Planning rate</Label>
          <Select value={value.rate_mode || 'verified_p25'}
                  onValueChange={(v) => onPatch({ rate_mode: v })}>
            <SelectTrigger className="h-8" data-testid={`rota-rate-mode-${pool}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RATE_MODES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {value.rate_mode === 'manual' &&
          field('Manual tasks / hour', 'manual_rate', { type: 'number', min: 0, step: '0.1' })}
      </div>
    </div>
  );
};

const PoolPreview = ({ pool, block }) => {
  const eq = block.equipment || {};
  const rate = block.planning_rate || {};
  const bt = block.backtest || {};
  const rec = block.reconciliation || {};
  const gap = block.weekly?.gap ?? 0;
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3" data-testid={`rota-preview-${pool}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-foreground">{block.label}</h4>
        <Badge variant={block.status === 'VALIDATED PLAN' ? 'default' : 'destructive'}
               className="text-[11px]" data-testid={`rota-status-${pool}`}>
          {block.status}
        </Badge>
      </div>

      <ul className="space-y-1.5" data-testid={`rota-gates-${pool}`}>
        {(block.gates || []).map((g) => (
          <li key={g.gate} className="flex items-start gap-2 text-xs">
            <GateIcon status={g.status} />
            <span className="text-foreground font-medium">{g.gate}</span>
            <span className="text-muted-foreground">— {g.detail}</span>
          </li>
        ))}
      </ul>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Required by demand', block.weekly?.required_people],
          ['People planned', block.weekly?.core_people],
          ['Gap (planned − required)', gap > 0 ? `+${gap}` : gap],
          ['Planning rate (tasks/h)', `${rate.value} → ${rate.effective_per_attendance_hour}/h on task`],
        ].map(([k, v], i) => (
          <div key={k} className="rounded-md bg-card border border-border p-3">
            <p className="text-[11px] text-muted-foreground">{k}</p>
            <p className={`text-lg font-semibold ${i === 2 ? (gap < 0 ? 'text-destructive' : 'text-emerald-600') : 'text-foreground'}`}
               data-testid={i === 2 ? `rota-gap-${pool}` : undefined}>
              {v ?? '—'}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {(block.bands || []).map((b) => (
          <Badge key={b.label} variant="secondary" className="font-mono text-[11px]">
            {b.people}× {b.label} {String(b.start).padStart(2, '0')}:00–{String(b.end % 24).padStart(2, '0')}:00
          </Badge>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-3 text-xs">
        <div className="flex items-start gap-2 text-muted-foreground">
          <Wrench className="h-3.5 w-3.5 mt-0.5" />
          <span>
            {eq.label}: peak {eq.peak_simultaneous} simultaneous · {eq.usable_units} usable
            ({eq.physical_units} − {eq.reserve_units} reserve)
            {eq.unmet_tasks_period
              ? <span className="text-destructive font-medium"> · {eq.unmet_tasks_period} tasks/period unmet</span>
              : <span className="text-emerald-600 font-medium"> · demand covered</span>}
          </span>
        </div>
        <div className="text-muted-foreground">
          Reconciliation: {rec.counted_in_grid}/{rec.source_tasks} tasks, difference{' '}
          <strong className={rec.difference ? 'text-destructive' : 'text-emerald-600'}>{rec.difference}</strong>
        </div>
        <div className="text-muted-foreground">
          Back-test: <strong className={bt.passed ? 'text-emerald-600' : 'text-destructive'}>
            {bt.days_cleared}/{bt.days_tested} days cleared
          </strong>{' '}· worst end-of-day backlog {bt.worst_end_backlog}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="text-xs w-full min-w-[520px]">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left font-medium py-1 pr-3">Weekday</th>
              {(block.bands || []).map((b) => <th key={b.label} className="text-center font-medium px-2">{b.label}</th>)}
              <th className="text-center font-medium px-2">People</th>
              <th className="text-center font-medium px-2">Peak sim.</th>
              <th className="text-center font-medium px-2">Unmet hrs</th>
            </tr>
          </thead>
          <tbody>
            {(block.staffing || []).map((s) => (
              <tr key={s.weekday} className="border-t border-border">
                <td className="py-1 pr-3 text-foreground">{s.weekday}</td>
                {(block.bands || []).map((b) => (
                  <td key={b.label} className="text-center px-2 text-foreground">{s.bands?.[b.label] ?? 0}</td>
                ))}
                <td className="text-center px-2 font-semibold text-foreground">{s.people}</td>
                <td className="text-center px-2 text-muted-foreground">{s.peak_simultaneous}</td>
                <td className={`text-center px-2 ${s.unmet_hours ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                  {s.unmet_hours}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground">
        {block.evening?.tasks} tasks ({block.evening?.share_pct}%) are confirmed after 19:00 and remain
        UNCLASSIFIED — {block.evening?.note}
      </p>
    </div>
  );
};

const RotaPlanCard = ({ period, poolParam }) => {
  const pools = poolParam && poolParam !== 'all' ? [poolParam] : ['grf', 'grt'];
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const ready = Boolean(period?.from && period?.to);

  useEffect(() => {
    if (!open || settings) return;
    getRotaSettings()
      .then(setSettings)
      .catch((e) => toast.error(e.message || 'Could not load rota settings'));
  }, [open, settings]);

  const patch = (pool, p) => setSettings((s) => ({ ...s, [pool]: { ...s[pool], ...p } }));

  const params = () => ({
    date_from: period.from,
    date_to: period.to,
    pool: poolParam || 'all',
    core_grf: settings?.grf?.core_people || 0,
    core_grt: settings?.grt?.core_people || 0,
    rate_grf: settings?.grf?.rate_mode === 'manual' ? (settings?.grf?.manual_rate || 0) : 0,
    rate_grt: settings?.grt?.rate_mode === 'manual' ? (settings?.grt?.manual_rate || 0) : 0,
  });

  const buildPlan = async () => {
    setBusy(true);
    try {
      for (const p of pools) {
        const s = settings?.[p] || {};
        await saveRotaSettings(p, {
          bands: s.bands,
          equipment_units: s.equipment_units,
          equipment_reserve: s.equipment_reserve,
          equipment_label: s.equipment_label,
          core_people: s.core_people,
          net_hours: s.net_hours,
          attendance_hours: s.attendance_hours,
          work_days_per_week: s.work_days_per_week,
          demand_percentile: s.demand_percentile,
          rate_mode: s.rate_mode,
          manual_rate: s.manual_rate,
        });
      }
      const res = await getRotaPlan(params());
      setPlan(res);
      setOpen(false);
      if (res.status === 'VALIDATED PLAN') toast.success('Validated plan built');
      else toast.warning('Built as a capacity scenario — some validation gates failed');
    } catch (e) {
      toast.error(e.message || 'Could not build the pack');
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setExporting(true);
    try {
      const blob = await exportRotaPlan(params());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `picking_capacity_${(poolParam || 'all')}_${period.from}_to_${period.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Capacity pack downloaded');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="rota-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-[#2D8C8C]" /> Planning rota &amp; capacity check
            </CardTitle>
            <CardDescription className="text-xs">
              Demand is reconciled to the source, planned on the P90 of real days with a rate measured
              from the data, and capped by the usable equipment. Every pack is back-tested hour by hour.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setOpen(true)} disabled={!ready} data-testid="rota-configure">
              <Sparkles className="h-4 w-4 mr-1.5" /> Build planning rota
            </Button>
            <Button variant="outline" onClick={download} disabled={!plan || exporting}
                    data-testid="rota-download"
                    title={plan ? 'Download the multi-sheet capacity pack' : 'Build the pack first'}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              {exporting ? 'Exporting…' : 'Download Excel'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!plan && (
          <p className="text-sm text-muted-foreground" data-testid="rota-empty">
            {ready
              ? 'Press Build planning rota to set the shift bands, equipment, planning rate and demand basis.'
              : 'Set a date range in the filter bar above first.'}
          </p>
        )}
        {plan && (
          <div className={`rounded-lg border p-3 text-sm ${plan.status === 'VALIDATED PLAN'
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : 'border-amber-300 bg-amber-50 text-amber-900'}`} data-testid="rota-overall-status">
            <strong>{plan.status}</strong>
            {plan.status !== 'VALIDATED PLAN' && (
              <span className="block text-xs mt-1">
                Use this pack as a diagnostic scenario, not a finished rota, until every failed gate below
                is resolved.
              </span>
            )}
          </div>
        )}
        {plan?.combined && (
          <div className="rounded-lg border border-border p-4" data-testid="rota-combined">
            <p className="text-sm font-semibold text-foreground mb-2">Both pools combined</p>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>People planned: <strong className="text-foreground">{plan.combined.people}</strong></span>
              <span>Required by demand: <strong className="text-foreground">{plan.combined.required_people}</strong></span>
              <span>Weekly shifts: <strong className="text-foreground">{plan.combined.weekly_shifts}</strong></span>
              <span>Weekly net hours: <strong className="text-foreground">{plan.combined.weekly_net_hours}</strong></span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {plan.combined.per_weekday.map((d) => (
                <Badge key={d.weekday} variant="outline" className="font-mono text-[11px]">
                  {d.weekday}: {d.people}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {plan && pools.map((p) => plan.pools?.[p] && (
          <PoolPreview key={p} pool={p} block={plan.pools[p]} />
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto" data-testid="rota-dialog">
          <DialogHeader>
            <DialogTitle>Planning rota setup</DialogTitle>
            <DialogDescription>
              {period?.from} → {period?.to}. The planning rate is measured from this window&rsquo;s data;
              a manual rate flags the pack as unverified. Settings are remembered for the next run.
            </DialogDescription>
          </DialogHeader>
          {!settings
            ? <p className="text-sm text-muted-foreground py-6">Loading settings…</p>
            : (
              <div className="space-y-4">
                {pools.map((p) => (
                  <PoolSettings key={p} pool={p} value={settings[p] || {}} onPatch={(x) => patch(p, x)} />
                ))}
              </div>
            )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="rota-dialog-cancel">Cancel</Button>
            <Button onClick={buildPlan} disabled={!settings || busy} data-testid="rota-dialog-build">
              {busy ? 'Building…' : 'Save & preview'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RotaPlanCard;
